import { revenirOuRemplacer } from '../router.js'

/**
 * Bus du SCEAU (finalisation, 11/10/2026) — calqué sur `stepup-bus.ts`.
 *
 * Un module métier (la Comptabilité) demande ici qu'un sceau se joue ; le
 * PROVIDER du shell (`SceauHost`, rendu par AppShell à côté des toasts)
 * enregistre le vrai lecteur. Le shell reste générique : c'est l'appelant qui
 * fournit les textes, la destination et les sélecteurs de la page d'arrivée.
 *
 * Le lecteur vit HORS de `.lc-route` : il survit au changement de page que le
 * sceau accompagne, là où tout ce que l'écran de départ rendrait mourrait.
 */

export interface Sceau {
  titre: string
  sousTitre: string
  /** Une pastille sous le texte (« Révision 2 », « Aperçu »). */
  puce: string | null
  /** La page d'arrivée — on y va par `revenirOuRemplacer` : jamais deux fois dans l'historique. */
  destination: string
  /** Sélecteur : la page d'arrivée est là. */
  pret: string
  /** Sélecteur attendu EN PLUS de `pret` (le visuel, pour ne pas mesurer avant qu'il ne pousse la pastille). */
  attendre: string | null
  /** Sélecteur : l'élément où le sceau se pose. */
  ancre: string
  /** Sélecteur : l'élément qui reçoit le focus à l'arrivée. */
  focus: string
  /** Classe posée un instant sur <html> quand le sceau se pose (l'accent d'arrivée). */
  classeArrivee: string | null
  /** Le texte du toast : l'annonce, la seule information que porte le sceau. */
  annonce: string
  /** D'où le sceau part (le bouton du geste) ; null : il naît sur place. */
  origine: DOMRect | null
  /** Couleur dominante qui teinte le voile ; null : le fond seul. */
  teinte: string | null
}

let joueur: ((s: Sceau) => boolean) | null = null
let enCours = false

export function setSceauPlayer(fn: ((s: Sceau) => boolean) | null): void {
  joueur = fn
  if (!fn) enCours = false
}

/**
 * Demande un sceau. Rend `false` s'il n'y a pas de lecteur, ou si un sceau se
 * joue déjà : l'appelant fait alors l'arrivée sans sceau lui-même.
 */
export function jouerSceau(s: Sceau): boolean {
  if (!joueur || enCours) return false
  enCours = true
  let pris = false
  try { pris = joueur(s) } catch { pris = false }
  // Le lecteur a pu finir de façon synchrone (mouvement minimal) : il a déjà
  // appelé `sceauTermine` — on ne repasse pas « en cours » derrière lui.
  if (!pris) enCours = false
  return pris
}

/** Le lecteur a fini (ou abandonné) : un autre sceau peut se jouer. */
export function sceauTermine(): void {
  enCours = false
}

/** Le focus se pose sur `focus` dès qu'il existe, tant que l'adresse est `destination` (1,5 s au plus). */
export function attendreFocus(focus: string, destination: string, limite = 1500): void {
  const debut = Date.now()
  const essai = (): void => {
    if (window.location.pathname !== destination) return
    const el = document.querySelector(focus)
    if (el instanceof HTMLElement) { el.focus({ preventScroll: true }); return }
    if (Date.now() - debut < limite) window.setTimeout(essai, 60)
  }
  window.setTimeout(essai, 60)
}

/**
 * L'ARRIVÉE SANS SCEAU — mouvement minimal, onglet caché, lecteur absent ou en
 * panne. La même destination, sans la mise en scène : la zone défilante
 * repart en haut (une redirection ne la remet pas à zéro), on revient à la
 * destination ou l'adresse est remplacée (`revenirOuRemplacer`), et le focus
 * suit.
 */
export function arriverSansSceau(destination: string, focus: string): void {
  document.querySelector('.lc-scroll')?.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior })
  revenirOuRemplacer(destination)
  attendreFocus(focus, destination)
}
